import { SVGProps, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ActionFlag, BuilderPage } from '@makyoapp/makyo-common'
import { Copy16, Download16, Eye24, EyeSlash24, Lock24, Refresh16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, MoreDropdown } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { SortableItem } from '@/components/SortableItem'

const UnlockedIcon = ({ width = 20, height = 20, ...props }: SVGProps<SVGSVGElement>) => {
  return (
    <svg width={width} height={height} viewBox='-1 0 25 24' xmlns='http://www.w3.org/2000/svg'>
      <rect x='3' y='11' width='18' height='11' rx='2' fill='transparent' stroke='currentColor' strokeWidth='1.5' />

      <path
        d='M8 11V7C8 4.79086 6.20914 3 4 3C1.79086 3 0 4.79086 0 7'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
      />

      <path
        d='M15.9966 15C15.4443 15 14.9966 15.4477 14.9966 16C14.9966 16.5523 15.4443 17 15.9966 17H16.0056C16.5578 17 17.0056 16.5523 17.0056 16C17.0056 15.4477 16.5578 15 16.0056 15H15.9966Z'
        fill='currentColor'
      />
      <path
        d='M10.9956 16C10.9956 15.4477 11.4433 15 11.9956 15H12.0046C12.5569 15 13.0046 15.4477 13.0046 16C13.0046 16.5523 12.5569 17 12.0046 17H11.9956C11.4433 17 10.9956 16.5523 10.9956 16Z'
        fill='currentColor'
      />
      <path
        d='M7.99463 15C7.44234 15 6.99463 15.4477 6.99463 16C6.99463 16.5523 7.44234 17 7.99463 17H8.00361C8.5559 17 9.00361 16.5523 9.00361 16C9.00361 15.4477 8.5559 15 8.00361 15H7.99463Z'
        fill='currentColor'
      />
    </svg>
  )
}

function SortablePage({
  page,
  isEdit,
  onFinishEdit,
  index,
}: {
  page: BuilderPage
  isEdit: boolean
  onFinishEdit?: () => void
  index: number
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState(page.title)

  const { setPageAction, exportToAiJSON } = useAppBuilderContext()
  const { activeLayer } = useActiveLayerContext()

  const { deletePage, clonePage, toggleVisibility, disablePage, updatePageAlt, makePrivate, goToPageSafely } =
    useAppPagesContext()
  const { closeAllOverlays } = useAppOverlaysContext()

  useEffect(() => {
    if (isEdit) {
      inputRef.current?.focus()
    }
  }, [isEdit, inputRef])

  const pageTitle = useVariableTextSingleCell(page.title)

  return (
    <SortableItem
      className={clsx(!page.enabled && 'opacity-50', activeLayer.id === page.id && 'bg-zinc-200 dark:bg-slate-700')}
      onClick={() => {
        if (page.slug) {
          goToPageSafely(page.slug)
        }
        closeAllOverlays()
      }}
      id={page.id}
      title={
        isEdit ? (
          <input
            className='bg-transparent border bdr w-full'
            ref={inputRef}
            id={`page-title-edit-${page.id}`}
            value={title}
            onFocus={(e) => {
              e.currentTarget.setSelectionRange(0, title.length)
            }}
            onChange={(e) => {
              setTitle(e.currentTarget.value)
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                inputRef.current?.blur()
              }
            }}
            onBlur={async () => {
              if (isEdit && title) {
                await updatePageAlt(index, (prevPage) => {
                  prevPage.title = title
                })
                onFinishEdit?.()
              }
            }}
          />
        ) : (
          <div className={clsx('txt')}>{pageTitle}</div>
        )
      }
      icon={
        <div className='min-w-[20px]'>
          {/* Add minimum width container */}
          <MaterialIcon size={20} icon={page.icon} />
        </div>
      }
      wrapTitle={false}
      reorderTitle={t('Reorder Screen')}
      onEdit={() => {
        setPageAction({
          flag: ActionFlag.Edit,
          page: page.id,
        })
        if (page.slug) {
          goToPageSafely(page.slug)
        }
      }}
      editTitle={t('Edit Screen')}
      deleteAtEnd={false}
    >
      <Button
        variant={ButtonVariant.Ghost}
        icon={page.private ? <Lock24 width={16} height={16} /> : <UnlockedIcon width={18} height={18} />}
        className='hover:text-primary-500!'
        noPadding
        squared
        onClick={async () => {
          await makePrivate(Boolean(page.private), page.id)
        }}
        title={page.private ? t('Private') : t('Public')}
        noTip
      />
      <Button
        variant={ButtonVariant.Ghost}
        icon={page.visible ? <Eye24 width={16} height={16} /> : <EyeSlash24 width={16} height={16} />}
        className='hover:text-primary-500!'
        noPadding
        squared
        onClick={async () => {
          await toggleVisibility(page.visible, page.id)
        }}
        title={page.visible ? t('Hide From Navigation Bar') : t('Show In Navigation Bar')}
        noTip
      />
      <MoreDropdown menuClassName='z-10 text-m-m' portalMenu placement='left' menuBtnClassName='w-9 h-9 flex-cc'>
        {page.type && (
          <MoreDropdown.MenuItem>
            <Refresh16 />
            <span>
              <Trans>Default Screen</Trans>
            </span>
          </MoreDropdown.MenuItem>
        )}
        <MoreDropdown.MenuItem
          onClick={async () => {
            await makePrivate(Boolean(page.private), page.id)
          }}
        >
          {page.private ? <UnlockedIcon width={18} height={18} /> : <Lock24 width={18} height={18} />}
          <span>{page.private ? <Trans>Make Public</Trans> : <Trans>Make Private</Trans>}</span>
        </MoreDropdown.MenuItem>
        <MoreDropdown.MenuItem
          onClick={async () => {
            await disablePage(page.enabled, page.id)
          }}
        >
          {page.enabled ? (
            <MaterialIcon size={20} icon='unpublished' />
          ) : (
            <MaterialIcon size={20} icon='check_circle' />
          )}
          <span>{page.enabled ? <Trans>Disable this screen</Trans> : <Trans>Enable this screen</Trans>}</span>
        </MoreDropdown.MenuItem>
        <MoreDropdown.MenuItem
          onClick={async () => {
            await exportToAiJSON('page', { id: page.id })
          }}
        >
          <Download16 />
          <span>
            <Trans>Export JSON</Trans>
          </span>
        </MoreDropdown.MenuItem>
        {!Boolean(page.tableId) && (
          <MoreDropdown.MenuItem
            onClick={async () => {
              await clonePage(page)
            }}
          >
            <Copy16 />
            <span>
              <Trans>Clone</Trans>
            </span>
          </MoreDropdown.MenuItem>
        )}
        {!page.type && (
          <MoreDropdown.MenuItem
            onClick={async () => {
              await deletePage(page.id)
            }}
          >
            <Trash16 />
            <span>
              <Trans>Delete</Trans>
            </span>
          </MoreDropdown.MenuItem>
        )}
      </MoreDropdown>
    </SortableItem>
  )
}

export default SortablePage
